import { DataTable, Dialog, Heading, Loader } from "@/components";
import { Button, Input } from "@/components/ui";
import { Search } from "lucide-react";
import useEmployee from "./useEmployeeMgmt";

const Employee = () => {
  const {
    onAddEmployee,
    globalFilter,
    setGlobalFilter,
    columns,
    employeeList,
    isEmployeeLoading,
    isMobile,
    setPageNumber,
    setPageSize,
    pageNumber,
    pageSize,
    isEmployeeRefetching,
    showInActiveConfirmation,
    setShowInActiveConfirmation,
    isUpdateEmployeePending,
    onActivateDeactivateEmployee,
    deactivationImpact,
    isDeactivationImpactLoading,
    isDeleteEmployeePending,
    selectedUser,
    setDeactivationId,
  } = useEmployee();

  return (
    <div className="p-4 md:p-6">
      {/* Header & Search */}
      <div className="flex flex-col lg:flex-row gap-2 lg:items-center lg:justify-between mb-6">
        <div className="flex flex-row items-center justify-between">
          <Heading title="Employee Management" />
          {isMobile && (
            <Button onClick={onAddEmployee} className="px-2">
              + Add Employee
            </Button>
          )}
        </div>
        <div className="flex flex-row items-center gap-4">
          {/* <Button className="bg-white text-g-500 border-g-200 border-1 hover:bg-white">
            <ListFilterIcon />
          </Button> */}
          <Input
            type="text"
            placeholder="Search"
            value={globalFilter ?? ""}
            onChange={(e) => setGlobalFilter(e.target.value)}
            className="w-full sm:w-72 bg-white h-[40px]"
            startIcon={<Search className="w-4 h-4" />}
          />
          {!isMobile && <Button onClick={onAddEmployee}>+ Add Employee</Button>}
        </div>
      </div>

      {/* Responsive Table */}
      {isEmployeeLoading || isEmployeeRefetching ? (
        <Loader />
      ) : (
        <>
          <DataTable
            columns={columns}
            data={employeeList?.data?.data || []}
            setGlobalFilter={setGlobalFilter}
            globalFilter={globalFilter}
            setPageNumber={setPageNumber}
            setPageSize={setPageSize}
            pageNumber={pageNumber}
            pageSize={pageSize}
            totalPages={Math.ceil(employeeList?.data?.total / pageSize)}
            totalRecords={employeeList?.data?.total}
            serviceSidePagination={true}
          />
        </>
      )}

      {showInActiveConfirmation && (
        <Dialog
          open={showInActiveConfirmation}
          setOpen={(open: boolean) => {
            setShowInActiveConfirmation(open);
            if (!open) {
              setDeactivationId("");
            }
          }}
          title={
            <div className="text-2xl font-bold text-secondary">
              {"Confirmation"}
              <div className="h-1 w-[30%] bg-secondary rounded-full" />
            </div>
          }
          saveText={
            selectedUser?.status?.toLowerCase() == "active"
              ? "Deactivate"
              : "Activate"
          }
          saveBtnClassName={
            selectedUser?.status?.toLowerCase() == "active"
              ? "bg-red-500 text-white hover:bg-red-500 hover:text-white"
              : "bg-green-600 text-white hover:bg-green-600 hover:text-white"
          }
          isLoading={isUpdateEmployeePending || isDeleteEmployeePending}
          onSave={onActivateDeactivateEmployee}
          contentClassName="!lg:w-[800px] lg:max-w-[800px]"
        >
          {selectedUser?.status?.toLowerCase() == "active" ? (
            isDeactivationImpactLoading ? (
              <div className="flex justify-center p-10">
                <Loader />
              </div>
            ) : (
              <div>
                {!deactivationImpact?.hasActiveAssignments ? (
                  <div className="text-lg py-4">
                    Are you sure you want to deactivate this user{" "}
                    <b>{selectedUser?.fullName || ""}</b>?
                  </div>
                ) : (
                  <div>
                    <b>❗ Critical Workflow Alert: Impacted Project Assignments </b>{" "}
                    <br />
                    <br />
                    Deactivating <b>{selectedUser?.fullName || ""}</b> will remove
                    their assigned responsibilities from the following active
                    projects:
                    <br />
                    <br />
                    <>
                      <div className="grid grid-cols-2 font-bold border-b pb-2 mb-2">
                        <div>Project Name</div>
                        <div>User's Role/Assignment</div>
                      </div>
                      <ul className="mb-4">
                        {deactivationImpact?.activeProjectAssignments?.map(
                          (item: any, index: number) => (
                            <li key={index} className="grid grid-cols-2 py-1">
                              <div>{item.projectName}</div>
                              <div>{item.roleName}</div>
                            </li>
                          )
                        )}
                      </ul>
                    </>
                    <b>Action Required:</b> You must ensure these assignments are
                    handled by an active user. <br />
                    <b>
                      Do you wish to proceed with deactivation, acknowledging that
                      you will immediately manage the reassignment of{" "}
                      {selectedUser?.fullName || ""}'s project duties?
                    </b>
                  </div>
                )}
              </div>

            )
          ) : (
            <div className="text-lg py-4">
              Are you sure you want to activate employee{" "}
              <b>{selectedUser?.fullName || ""}</b>?
            </div>
          )}
        </Dialog>
      )}
    </div>
  );
};

export default Employee;
